import { useEffect, useState } from 'react';
import { GoogleMap, useLoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { Store, StoreOpeningTime } from 'shared/types/cart';
import Button from 'components/commercetools-ui/atoms/button';
import Loading from 'components/commercetools-ui/atoms/loading';
import { tablet } from 'helpers/utils/screensizes';
import markerIcon from '/public/images/amricangolf_map_marker_icon.png';

interface GoogleMapProps {
  stores: Store[];
  selectedStore: Store | null;
  setSelectedStore: (store: Store | null) => void;
  handleMarkerClick: (store: Store) => void;
  postcode: string;
  setSearchKeyword: (arg: string) => void;
  storeOpeningTime: StoreOpeningTime | null;
  setStoreOpeningTime: (arg: StoreOpeningTime | null) => void;
  handleStoreAddress: () => void;
}

const containerStyle = {
  width: '100%',
  height: 'inherit',
};

const center = {
  lat: 55.3781,
  lng: -3.436,
};

const GoogleMapComponent: React.FC<GoogleMapProps> = ({
  stores,
  selectedStore,
  setSelectedStore,
  handleMarkerClick,
  postcode,
  setSearchKeyword,
  storeOpeningTime,
  setStoreOpeningTime,
  handleStoreAddress,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string,
  });
  const [mapCenter, setMapCenter] = useState(center);
  const [refresh, setRefresh] = useState(0);
  const [mapZoom, setMapZoom] = useState(5);

  useEffect(() => {
    const geocodePostcode = async (postcode: string) => {
      if (!postcode) return;

      try {
        const geocoder = new google.maps.Geocoder();
        const response = await geocoder.geocode({ address: postcode });
        const { location } = response.results[0].geometry;
        setMapCenter(location.toJSON());
        setMapZoom(9);
      } catch (error) {
        console.error('Geocoding error: ', error);
      }
    };

    geocodePostcode(postcode);
  }, [postcode]);

  useEffect(() => {
    if (selectedStore) {
      setSearchKeyword(selectedStore.postalCode || selectedStore.city);
    }

    if (!selectedStore && !postcode) {
      setMapZoom(5);
    }
  }, [selectedStore, setSearchKeyword, postcode]);

  useEffect(() => {
    setTimeout(() => {
      setRefresh(1);
    }, 1000);
  }, []);

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return (
      <div className="relative mt-[280px]">
        <Loading></Loading>
      </div>
    );
  }

  const icon = markerIcon
    ? {
        url: markerIcon?.src,
        scaledSize: new google.maps.Size(26, 36),
      }
    : '';

  return (
    <>
      {isLoaded && (
        <div className="h-[45vh] md:h-[580px]">
          <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={mapZoom}>
            {stores.length > 0 &&
              stores?.map((store) => {
                return (
                  <Marker
                    key={`${refresh}_${store.ID}`}
                    position={{ lat: store.latitude, lng: store.longitude }}
                    onClick={() => {
                      handleMarkerClick(store);
                      setStoreOpeningTime(null);
                    }}
                    icon={icon}
                  />
                );
              })}

            {selectedStore && window.innerWidth > tablet && (
              <div className="hidden md:block md:max-w-[200px]">
                {storeOpeningTime && (
                  <InfoWindow
                    position={{
                      lat: selectedStore.latitude,
                      lng: selectedStore.longitude,
                    }}
                    onCloseClick={() => setSelectedStore(null)}
                  >
                    <div className="hidden p-4 md:block">
                      <h2 className=" text-lg font-medium">{selectedStore.name}</h2>
                      <p className="mt-4 !max-w-[240px] font-light">{selectedStore.listAddress}</p>
                      <div className="mt-18">
                        <table>
                          {storeOpeningTime.storeHours &&
                            Object?.entries(storeOpeningTime.storeHours).map(([dayKey, hours]) => {
                              const dayName = storeOpeningTime.hourResources
                                ? storeOpeningTime.hourResources[dayKey]
                                : dayKey;

                              return (
                                <tr key={dayKey} className="border-b">
                                  <td className="py-4 text-xs font-medium capitalize">{dayName}</td>
                                  <td className="py-4 text-right text-xs font-light">{hours}</td>
                                </tr>
                              );
                            })}
                        </table>
                      </div>

                      <div className="mt-20 w-full px-20 py-10 text-center">
                        <Button onClick={handleStoreAddress} size="s">
                          {selectedStore.resources.storeDetails}
                        </Button>
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </div>
            )}
          </GoogleMap>
        </div>
      )}
    </>
  );
};

export default GoogleMapComponent;
